<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="me-2 mb-2">
            <div class="d-flex align-items-center border bg-white rounded p-1 me-2">
              <router-link :to="{ name: 'crm.companies.list' }" class="btn btn-icon btn-sm me-1">
                <i class="ti ti-list-tree"></i>
              </router-link>
              <router-link :to="{ name: 'crm.companies.grid' }" class="btn btn-icon btn-sm active bg-primary text-white">
                <i class="ti ti-layout-grid"></i>
              </router-link>
            </div>
          </div>
          <div class="me-2 mb-2">
            <button class="btn btn-white d-inline-flex align-items-center" type="button" @click="refreshCompanyList">
              <i class="ti ti-refresh me-1"></i>Refresh
            </button>
          </div>
          <div class="head-icons ms-2">
            <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body p-3">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <h5 class="mb-0">Companies Grid</h5>
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <input
                v-model.trim="search"
                type="text"
                class="form-control"
                placeholder="Search company, email, or phone"
              />
              <select v-model="sortBy" class="form-select">
                <option value="recent">Recently Added</option>
                <option value="name_asc">Name A-Z</option>
                <option value="name_desc">Name Z-A</option>
              </select>
            </div>
          </div>
          <div class="d-flex align-items-center justify-content-between mt-3 text-muted">
            <small>{{ filteredCountLabel }}</small>
            <small>CRM companies are read-only here.</small>
          </div>
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-content-center align-items-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="!filteredCompanies.length" class="text-center py-5">
        <div class="mb-4">
          <i class="ti ti-building fs-48 text-muted"></i>
        </div>
        <h5 class="mb-2">No Companies Found</h5>
        <p class="text-muted mb-0">No company matches the current filter.</p>
      </div>

      <div v-else class="row">
        <div
          v-for="company in filteredCompanies"
          :key="company.id"
          class="col-xl-3 col-lg-4 col-md-6 mb-4"
        >
          <div class="card h-100">
            <div class="card-body">
              <div class="d-flex justify-content-center mb-3">
                <router-link
                  :to="{ name: 'crm.companies.details', params: { id: company.id } }"
                  class="avatar avatar-xl avatar-rounded border rounded-circle overflow-hidden d-inline-flex align-items-center justify-content-center bg-light text-dark"
                >
                  <img
                    v-if="company.logo_url"
                    :src="company.logo_url"
                    class="img-fluid h-auto w-auto"
                    :alt="company.company_name"
                  />
                  <span v-else>{{ initials(company.company_name) }}</span>
                </router-link>
              </div>

              <div class="text-center mb-3">
                <h6 class="mb-1">
                  <router-link :to="{ name: 'crm.companies.details', params: { id: company.id } }">
                    {{ company.company_name }}
                  </router-link>
                </h6>
                <span :class="statusClass(company.status)">
                  <i class="ti ti-point-filled me-1"></i>{{ company.status }}
                </span>

                <div v-if="company.employees && company.employees.length" class="avatar-list-stacked avatar-group-sm mt-2">
                  <span
                    v-for="(employee, index) in company.employees.slice(0, 5)"
                    :key="employee.id || index"
                    class="avatar avatar-rounded overflow-hidden"
                  >
                    <img
                      v-if="employee.avatar"
                      class="border border-white"
                      :src="employee.avatar"
                      :alt="employee.name || 'Employee'"
                    />
                    <span v-else class="d-inline-flex w-100 h-100 align-items-center justify-content-center bg-light text-dark">
                      {{ initials(employee.name || employee.email || 'Employee') }}
                    </span>
                  </span>
                  <span
                    v-if="company.employees.length > 5"
                    class="avatar bg-primary avatar-rounded text-fixed-white fs-12"
                  >
                    +{{ company.employees.length - 5 }}
                  </span>
                </div>
              </div>

              <div class="d-flex flex-column">
                <p v-if="company.email" class="text-dark d-inline-flex align-items-center mb-2">
                  <i class="ti ti-mail-forward text-gray-5 me-2"></i>
                  {{ company.email }}
                </p>
                <p v-if="company.phone" class="text-dark d-inline-flex align-items-center mb-2">
                  <i class="ti ti-phone text-gray-5 me-2"></i>
                  {{ company.phone }}
                </p>
                <p v-if="locationLabel(company) !== '-'" class="text-dark d-inline-flex align-items-center">
                  <i class="ti ti-map-pin text-gray-5 me-2"></i>
                  {{ locationLabel(company) }}
                </p>
              </div>

              <div class="d-flex align-items-center justify-content-between border-top pt-3 mt-3">
                <div class="icons-social d-flex align-items-center">
                  <a
                    v-if="company.email"
                    :href="`mailto:${company.email}`"
                    class="avatar avatar-rounded avatar-sm me-1"
                  >
                    <i class="ti ti-mail"></i>
                  </a>
                  <a
                    v-if="company.phone"
                    :href="`tel:${company.phone}`"
                    class="avatar avatar-rounded avatar-sm me-1"
                  >
                    <i class="ti ti-phone-call"></i>
                  </a>
                  <a
                    v-if="company.website_url"
                    :href="normalizeUrl(company.website_url)"
                    class="avatar avatar-rounded avatar-sm me-1"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i class="ti ti-world"></i>
                  </a>
                </div>
                <router-link :to="{ name: 'crm.companies.details', params: { id: company.id } }" class="btn btn-sm btn-light">
                  View
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useCompanyStore } from "@/stores/companyStore";

const title = ref("Companies");
const text = ref("CRM");
const text1 = ref("Companies Grid");

const companyStore = useCompanyStore();
const loading = computed(() => companyStore.loading);
const search = ref("");
const sortBy = ref("recent");

const filteredCompanies = computed(() => {
  let companies = [...(companyStore.companies || [])];

  if (search.value) {
    const needle = search.value.toLowerCase();
    companies = companies.filter((company) =>
      [company.company_name, company.email, company.phone, company.phone_two]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
  }

  switch (sortBy.value) {
    case "name_asc":
      companies.sort((a, b) => String(a.company_name || "").localeCompare(String(b.company_name || "")));
      break;
    case "name_desc":
      companies.sort((a, b) => String(b.company_name || "").localeCompare(String(a.company_name || "")));
      break;
    default:
      companies.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      break;
  }

  return companies;
});

const filteredCountLabel = computed(() => {
  const total = companyStore.companies.length;
  const filtered = filteredCompanies.value.length;
  return filtered === total ? `${total} companies` : `${filtered} of ${total} companies`;
});

async function refreshCompanyList() {
  await companyStore.fetchCompanies({ all: true });
}

const toggleHeader = () => {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
};

const statusClass = (status) =>
  status === "Active"
    ? "badge badge-success d-inline-flex align-items-center badge-xs"
    : status === "Suspended"
      ? "badge badge-warning d-inline-flex align-items-center badge-xs"
      : "badge badge-danger d-inline-flex align-items-center badge-xs";

function initials(value = "") {
  return String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "CO";
}

function locationLabel(company) {
  return [company.address, company.state, company.country].filter(Boolean).join(", ") || company.country_code || "-";
}

function normalizeUrl(value) {
  if (!value) return "#";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

onMounted(async () => {
  await refreshCompanyList();
});
</script>

<style scoped>
.avatar-list-stacked {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.5rem;
}

.avatar-list-stacked .avatar {
  margin-left: -0.25rem;
}

.avatar-list-stacked .avatar:first-child {
  margin-left: 0;
}

.icons-social a {
  transition: all 0.2s ease-in-out;
}

.icons-social a:hover {
  transform: scale(1.1);
  background-color: var(--bs-primary);
  color: white !important;
}
</style>


